/**
 * Design System Theme for Lobby2.0
 * Replaces LESS variables with styled-components theme
 */

import { GAME_CARD_ASPECT_RATIO } from "../constants";

export const theme = {
  // Colors
  colors: {
    // Primary brand colors
    primary: '#ff0000',
    primaryHover: '#0056b3',
    primaryActive: '#004085',
    
    // Secondary colors
    secondary: '#6c757d',
    secondaryHover: '#545b62',
    secondaryActive: '#3d4142',
    
    // Status colors
    success: '#28a745',
    danger: '#dc3545',
    warning: '#ffc107',
    info: '#17a2b8',
    
    // Neutral colors
    light: '#f8f9fa',
    dark: '#343a40',
    white: '#ffffff',
    black: '#000000',
    
    // Game specific colors
    cardBackground: '#1a1a1a',
    cardBackgroundHover: '#2a2a2a',
    cardBackgroundActive: '#3a3a3a',
    cardBorder: '#333333',
    cardBorderHover: '#444444',
    
    // Jackpot colors
    jackpotGold: '#ffd700',
    jackpotSilver: '#c0c0c0',
    jackpotBronze: '#cd7f32',
    
    // Background colors
    appBackground: '#2e2e2e',
    surface: '#1a1a1a',
    modalBackground: 'rgba(0, 0, 0, 0.8)',
    overlayBackground: 'rgba(0, 0, 0, 0.6)',
    
    // Text colors
    textPrimary: '#ffffff',
    textSecondary: '#b0b0b0',
    textMuted: '#6c757d',
    textInverse: '#000000',
    
    // Border colors
    border: '#333333',
    borderLight: '#555555',
    borderDark: '#222222',

    // Navigation colors
    piliotGreen: '#3CFF00'
  },
  
  // Typography
  typography: {
    fontFamily: {
      primary: '"Futura PT", Roboto',
      secondary: '"Futura PT Cond", Roboto',
      tertiary: '"Kranto", Impact, "Arial Black", sans-serif',
      quaternary: '"San Francisco Bold", Roboto',
      numeric: '"Numeric", Roboto',
      roboto: 'Roboto',
    },
    fontSizes: {
      xs: '0.75rem',   // 12px
      sm: '0.875rem',  // 14px
      md: '1rem',      // 16px
      lg: '1.125rem',  // 18px
      xl: '1.25rem',   // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
    },
    fontWeights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeights: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  
  // Spacing (using 8px base unit)
  spacing: {
    '0': '0',
    '1': '0.25rem', // 4px
    '2': '0.5rem',  // 8px
    '3': '0.75rem', // 12px
    '4': '1rem',    // 16px
    '5': '1.25rem', // 20px
    '6': '1.5rem',  // 24px
    '8': '2rem',    // 32px
    '10': '2.5rem', // 40px
    '12': '3rem',   // 48px
    '16': '4rem',   // 64px
    '20': '5rem',   // 80px
    '24': '6rem',   // 96px
  },
  
  // Border radius
  borderRadius: {
    none: '0',
    sm: '0.25rem',   // 4px
    md: '0.375rem',  // 6px
    lg: '0.5rem',    // 8px
    xl: '0.75rem',   // 12px
    '2xl': '1rem',   // 16px
    '3xl': '1.5rem', // 24px
    full: '9999px',
  },
  
  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    none: 'none',
    
    // Game specific shadows
    cardHover: '0 8px 16px rgba(0, 0, 0, 0.3)',
    cardActive: '0 4px 8px rgba(0, 0, 0, 0.4)',
    jackpot: '0 0 20px rgba(255, 215, 0, 0.3)',
  },
  
  // Breakpoints (mobile-first) - Based on old lobby patterns
  breakpoints: {
    xs: '320px',   // Small phones
    xs1: '375px',  // iPhone SE/8
    xs2: '390px',  // iPhone 12 Mini
    xs3: '414px',  // iPhone 6+/7+/8+
    xs4: '430px',  // iPhone 14 Pro Max
    sm: '576px',   // Large phones
    md: '768px',   // Tablets
    lg: '1025px',  // Desktops
    xl: '1366px',  // Large desktops
    '2xl': '1600px', // Extra large screens
  },
  
  // Mobile-specific breakpoints for media queries
  media: {
    xs: '@media (max-width: 320px)',
    xs1: '@media (max-width: 375px)',
    xs2: '@media (max-width: 390px)',
    xs3: '@media (max-width: 414px)',
    xs4: '@media (max-width: 430px)',
    sm: '@media (max-width: 576px)',
    md: '@media (max-width: 768px)',
    lg: '@media (max-width: 1025px)',
    xl: '@media (max-width: 1366px)',
    
    // Min-width queries for mobile-first approach
    smUp: '@media (min-width: 577px)',
    mdUp: '@media (min-width: 769px)',
    lgUp: '@media (min-width: 1026px)',
    xlUp: '@media (min-width: 1367px)',
    
    // Orientation queries
    portrait: '@media (orientation: portrait)',
    landscape: '@media (orientation: landscape)',

    // Orientation and width
    portraitMd: '@media (orientation: portrait) and (max-width: 768px)',
    landscapeLg: '@media (orientation: landscape) and (max-width: 1025px)',

    // Orientation and height
    landscapeXs4H: '@media (orientation: landscape) and (max-height: 430px)',
    
    // Touch device detection
    touch: '@media (hover: none) and (pointer: coarse)',
    hover: '@media (hover: hover) and (pointer: fine)',
    
    // High DPI displays (Retina)
    retina: '@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)',
  },
  
  // Z-index scale
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },
  
  // Transitions and animations
  transitions: {
    duration: {
      fastest: '150ms',
      fast: '200ms',
      normal: '300ms',
      slow: '500ms',
      slowest: '800ms',
    },
    easing: {
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
      linear: 'linear',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },
  },
  
  // Layout
  layout: {
    containerMaxWidth: '1200px',
    sidebarWidth: '280px',
    headerHeight: '64px',
    footerHeight: '48px',
    statusBarHeight: '44px',
    
    // Mobile-first game card sizing
    gameCard: {
      mobile: {
        width: '132px',
        height: '182px',
        aspectRatio: `1 / ${GAME_CARD_ASPECT_RATIO}`
      },
      mobileLarge: {
        width: '140px',
        height: '193px'
      },
      tablet: {
        width: '164px',
        height: '226px'
      },
      desktop: {
        width: '200px',
        height: '276px'
      },
      desktopLarge: {
        width: '246px',
        height: '339px'
      }
    },
    
    // Mobile navigation
    navigation: {
      mobile: {
        height: '70px',
        padding: '8px',
        tabMinWidth: '60px'
      },
      desktop: {
        height: '80px',
        padding: '12px',
        tabMinWidth: '80px'
      }
    },
    
    // Grid system - matching old lobby responsive behavior
    grid: {
      mobile: {
        columns: 2,
        gap: '12px',
        padding: '16px'
      },
      mobileLarge: {
        columns: 3,
        gap: '14px',
        padding: '20px'
      },
      tablet: {
        columns: 4,
        gap: '16px',
        padding: '24px'
      },
      desktop: {
        columns: 5,
        gap: '20px',
        padding: '32px'
      },
      desktopLarge: {
        columns: 6,
        gap: '20px',
        padding: '32px'
      }
    }
  },
  
  // Game specific
  game: {
    cardAspectRatio: '4/3',
    thumbnailAspectRatio: '16/9',
    iconSize: '48px',
    badgeSize: '24px',
  },
  
  // Mobile touch and interaction
  touch: {
    // Touch target minimum sizes (iOS Human Interface Guidelines)
    minTouchTarget: '44px',
    comfortableTouchTarget: '48px',
    
    // Touch-friendly spacing
    touchSpacing: {
      xs: '8px',
      sm: '12px',
      md: '16px',
      lg: '24px'
    },
    
    // iOS-specific properties
    ios: {
      tapHighlightColor: 'transparent',
      touchCallout: 'none',
      userSelect: 'none',
      overflowScrolling: 'touch',
      overscrollBehavior: 'auto'
    }
  },
} as const;

export type Theme = typeof theme;
